import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ConfirmDialog from '../../../../components/ConfirmDialog/ConfirmDialog';
import { SendAdminCompanyMessageAPI } from '../../../../api/AdminAPI';

/** Mesmos limites de CompanyMessageSender.php (backend). */
const CHANNELS = [
  { key: 'email', label: 'E-mail', prose: 'e-mail', maxLength: 10000, hint: 'Enviado pela caixa de suporte da Gabriela, que recebe a resposta da clínica.' },
  { key: 'whatsapp', label: 'WhatsApp', prose: 'WhatsApp', maxLength: 4000, hint: 'Enviado na hora pelo número de WhatsApp da Gabriela.' },
];
const BOTH_CHANNELS_HINT = 'Mesmo texto nos dois canais: e-mail pela caixa de suporte da Gabriela e WhatsApp na hora pelo número da Gabriela.';
const SUBJECT_MAX_LENGTH = 150;

/** Motivo do botão "Enviar mensagem" desabilitado na página da empresa. */
export const NO_CONTACTS_HINT = 'A empresa não tem e-mail nem telefone cadastrados para receber mensagens.';

function contactsOf(messageContacts) {
  return {
    email: messageContacts?.email || [],
    whatsapp: messageContacts?.whatsapp || [],
  };
}

/** Há pelo menos um destinatário em algum canal (habilita o botão "Enviar mensagem"). */
export function hasCompanyMessageContacts(company) {
  const contacts = contactsOf(company?.message_contacts);
  return contacts.email.length > 0 || contacts.whatsapp.length > 0;
}

/**
 * "por e-mail para A, B e por WhatsApp para C" a partir do que foi entregue
 * ([{ channel, to_label }]); usado no diálogo e no aviso da página.
 */
export function describeDeliveries(deliveries) {
  return CHANNELS
    .map((item) => ({
      item,
      names: (deliveries || []).filter((d) => d.channel === item.key).map((d) => d.to_label || 'a empresa'),
    }))
    .filter((group) => group.names.length > 0)
    .map((group) => `por ${group.item.prose} para ${group.names.join(', ')}`)
    .join(' e ');
}

/** Primeiro destinatário de cada canal (o backend já devolve a lista na ordem de preferência). */
function defaultRecipients(contacts) {
  return {
    email: contacts.email[0] ? [contacts.email[0].key] : [],
    whatsapp: contacts.whatsapp[0] ? [contacts.whatsapp[0].key] : [],
  };
}

const deliveryId = (channelKey, recipientKey) => `${channelKey}|${recipientKey}`;

/**
 * SendCompanyMessageDialog - Mensagem avulsa do Admin Geral para uma empresa
 *
 * Canais (e-mail, WhatsApp ou os dois com o mesmo texto) e, em cada canal, um ou
 * mais destinatários escolhidos só entre os contatos cadastrados da empresa
 * (`company.message_contacts`, de admin/companies/get.php: telefone do cadastro,
 * usuários, agenda pública), assunto (e-mail) e texto. Cada destinatário é uma
 * chamada a send_message.php (e-mails antes dos WhatsApps, na ordem da lista);
 * se uma falhar, as anteriores ficam marcadas como enviadas e o botão reenvia só
 * o que faltou. O envio sai pelos canais da Gabriela e fica na auditoria
 * `company.send_message`, que é o histórico linkado no rodapé.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {{ id: number|string, name: string, message_contacts?: Object }|null} props.company
 * @param {Function} props.onCancel
 * @param {Function} props.onSent - chamado com a lista [{ channel, to_label }] do que foi enviado
 */
function SendCompanyMessageDialog({ isOpen, company, onCancel, onSent }) {
  const [channels, setChannels] = useState(['email']);
  const [recipients, setRecipients] = useState({ email: [], whatsapp: [] });
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  // Entregas já feitas nesta abertura do diálogo (deliveryId -> { channel, to_label }).
  const [sent, setSent] = useState({});

  const messageContacts = company?.message_contacts;
  const contacts = contactsOf(messageContacts);

  // Ao abrir: primeiro canal com destinatário, primeiro destinatário de cada canal, campos limpos.
  useEffect(() => {
    if (!isOpen) return;
    const initial = contactsOf(messageContacts);
    const firstChannel = CHANNELS.find((item) => initial[item.key].length > 0)?.key || 'email';
    setChannels([firstChannel]);
    setRecipients(defaultRecipients(initial));
    setSubject('');
    setMessage('');
    setError('');
    setSending(false);
    setSent({});
  }, [isOpen, messageContacts]);

  const selected = CHANNELS.filter((item) => channels.includes(item.key));
  const sentList = Object.values(sent);
  const pending = selected.flatMap((item) =>
    recipients[item.key]
      .filter((key) => !sent[deliveryId(item.key, key)])
      .map((key) => ({ item, key }))
  );
  const isEmail = channels.includes('email');
  const maxLength = Math.min(...selected.map((item) => item.maxLength));
  const hint = selected.length > 1 ? BOTH_CHANNELS_HINT : (selected[0]?.hint || '');
  const canSend =
    pending.length > 0 &&
    selected.every((item) => recipients[item.key].length > 0) &&
    message.trim() !== '' &&
    message.length <= maxLength &&
    (!isEmail || (subject.trim() !== '' && subject.length <= SUBJECT_MAX_LENGTH));

  const labelOf = (item, key) => contacts[item.key].find((contact) => contact.key === key)?.label || key;
  const channelHasSent = (item) => sentList.some((d) => d.channel === item.key);

  const handleChannelsChange = (_event, next) => {
    if (!next || next.length === 0) return;
    setChannels(next);
    setError('');
  };

  // Mantém a ordem da lista de contatos (que é a ordem de preferência do backend).
  const handleRecipientsChange = (item, value) => {
    const chosen = Array.isArray(value) ? value : [value];
    const ordered = contacts[item.key].filter((contact) => chosen.includes(contact.key)).map((contact) => contact.key);
    setRecipients((prev) => ({ ...prev, [item.key]: ordered }));
    setError('');
  };

  const payloadFor = ({ item, key }) => ({
    company_id: company.id,
    channel: item.key,
    recipient: key,
    subject: item.key === 'email' ? subject.trim() : '',
    message: message.trim(),
  });

  // Um destinatário por vez, parando no primeiro erro; o que já saiu fica em `sent`.
  const handleConfirm = () => {
    if (!canSend || sending) return;
    setSending(true);
    setError('');
    const queue = [...pending];
    const delivered = { ...sent };
    const sendNext = () => {
      const delivery = queue.shift();
      if (!delivery) {
        setSending(false);
        if (onSent) onSent(Object.values(delivered));
        return;
      }
      SendAdminCompanyMessageAPI(payloadFor(delivery), (response) => {
        const label = labelOf(delivery.item, delivery.key);
        if (!response || response.success === false) {
          setSent(delivered);
          setSending(false);
          setError(`${delivery.item.label} para ${label}: ${response?.error || 'Erro ao enviar a mensagem. Tente novamente.'}`);
          return;
        }
        delivered[deliveryId(delivery.item.key, delivery.key)] = {
          channel: delivery.item.key,
          to_label: response.to_label || label,
        };
        sendNext();
      });
    };
    sendNext();
  };

  const renderRecipientChips = (item) => (values) => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {values.map((key) => (
        <Chip key={key} label={labelOf(item, key)} size="small" variant="outlined" color="primary" />
      ))}
    </Box>
  );

  return (
    <ConfirmDialog
      isOpen={isOpen}
      title="Enviar mensagem para a empresa"
      variant="info"
      confirmLabel={sentList.length > 0 ? 'Enviar o restante' : 'Enviar'}
      cancelLabel="Cancelar"
      onConfirm={handleConfirm}
      onCancel={onCancel}
      confirmDisabled={!canSend}
      loading={sending}
      maxWidth={560}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {company?.name && (
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
            {company.name}
          </Typography>
        )}

        <Box>
          <ToggleButtonGroup value={channels} size="small" onChange={handleChannelsChange} disabled={sending}>
            {CHANNELS.map((item) => (
              <ToggleButton
                key={item.key}
                value={item.key}
                disabled={contacts[item.key].length === 0 || channelHasSent(item)}
                sx={{ px: 2, textTransform: 'none' }}
              >
                {item.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Escolha um canal ou os dois.
          </Typography>
        </Box>

        {selected.map((item) => {
          const options = contacts[item.key];
          const values = recipients[item.key];
          return (
            <TextField
              key={item.key}
              select
              size="small"
              label={selected.length > 1 ? `Destinatários do ${item.label}` : 'Destinatários'}
              value={values}
              onChange={(e) => handleRecipientsChange(item, e.target.value)}
              fullWidth
              disabled={sending || options.length === 0}
              helperText={
                options.length === 0
                  ? `Sem ${item.label.toLowerCase()} cadastrado para esta empresa.`
                  : 'Só contatos já cadastrados na empresa. Pode marcar mais de um.'
              }
              SelectProps={{ multiple: true, renderValue: renderRecipientChips(item) }}
            >
              {options.map((contact) => {
                const alreadySent = !!sent[deliveryId(item.key, contact.key)];
                return (
                  <MenuItem key={contact.key} value={contact.key} disabled={alreadySent}>
                    <Checkbox size="small" checked={values.includes(contact.key)} sx={{ py: 0, pl: 0 }} />
                    <ListItemText primary={contact.label} secondary={alreadySent ? 'Já enviado' : contact.value} />
                  </MenuItem>
                );
              })}
            </TextField>
          );
        })}

        {isEmail && (
          <TextField
            size="small"
            label="Assunto"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            fullWidth
            disabled={sending || channelHasSent(CHANNELS[0])}
            inputProps={{ maxLength: SUBJECT_MAX_LENGTH }}
          />
        )}

        <TextField
          label="Mensagem"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          fullWidth
          multiline
          minRows={6}
          maxRows={14}
          disabled={sending}
          error={message.length > maxLength}
          inputProps={{ maxLength }}
          helperText={`${hint} ${message.length}/${maxLength}`}
        />

        {sentList.length > 0 && (
          <Alert severity="success">{`Enviado ${describeDeliveries(sentList)}.`}</Alert>
        )}

        {error && <Alert severity="error">{error}</Alert>}

        {company && company.id !== undefined && (
          <Link
            component={RouterLink}
            to={`/admin/audit?company=${company.id}&action=company.send_message`}
            variant="body2"
            sx={{ alignSelf: 'flex-start' }}
          >
            Ver mensagens já enviadas para esta empresa
          </Link>
        )}
      </Box>
    </ConfirmDialog>
  );
}

export default SendCompanyMessageDialog;
